/**
 * Saúde do acervo, portada de knowledge.js:596-702.
 *
 * É a tela que decide se o time confia no acervo, e por isso ela mostra o que
 * está ruim antes do que está bom: cobertura, doc atrás do HEAD, arquivo sem
 * dono, lacuna aberta, busca que não achou nada. Número bonito sem essas listas
 * viraria placar, não diagnóstico.
 *
 * "Memória viva" existe pela razão oposta à cobertura: acervo cresce sozinho com
 * doc gerada, mas só é confiável se alguém revalida — nota cujo trecho mudou,
 * resumo de sessão velho, busca sem resposta e ambiente fora do commit indexado.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apagar, postar, remendar } from '../api/cliente';
import { chaves, useDonos, useLacunas, useSaude } from '../api/consultas';
import { useSessao } from '../auth/sessao';
import { acionavel } from '../componentes/acionavel';
import { Carregando, Falha } from '../componentes/Estado';
import { copiar, useRecado } from '../componentes/Recado';
import { ESPECIE_DE_LACUNA, rotulo, shaCurto } from '../dados/rotulos';

export function Saude({ slug }: { slug: string }) {
  const avisar = useRecado();
  const navegar = useNavigate();
  const fila = useQueryClient();
  const { admin } = useSessao();
  const saude = useSaude(slug);
  const lacunas = useLacunas(slug);
  const donos = useDonos(slug);

  const reindexar = useMutation({
    mutationFn: () => postar<{ indexed?: number; total?: number }>(`/api/${slug}/reindex`),
    onSuccess: async (r) => {
      avisar(r.indexed
        ? `${r.indexed} de ${r.total} unidade(s) reindexada(s)`
        : r.total
          ? `nada a fazer: as ${r.total} unidades já estavam indexadas`
          : 'nenhuma unidade para indexar');
      await fila.invalidateQueries({ queryKey: chaves.saude(slug) });
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  const fecharLacuna = useMutation({
    mutationFn: (id: string) => remendar(`/api/${slug}/gaps/${encodeURIComponent(id)}`, { status: 'done' }),
    onSuccess: async () => {
      avisar('lacuna fechada');
      await Promise.all([
        fila.invalidateQueries({ queryKey: chaves.lacunas(slug) }),
        fila.invalidateQueries({ queryKey: chaves.saude(slug) }),
      ]);
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  if (saude.isPending) return <Carregando rotulo="a saúde do acervo" />;
  if (saude.error) {
    return <Falha erro={saude.error} rotulo="a saúde do acervo" aoRepetir={saude.refetch} />;
  }

  const h = saude.data!;
  const t = h.totals;
  const rc = h.search_recall || {};
  const env = h.environment || {};
  const sessoesVelhas = h.stale_sessions || [];
  const notasARevisar = h.notes_to_review || [];

  const abrirDoc = (k: string) => navegar(`/p/${slug}/doc?key=${encodeURIComponent(k)}`);
  const Chave = ({ k, porque }: { k: string; porque?: string }) => (
    <div className="hrow">
      <code title={k} {...acionavel(() => k && abrirDoc(k))}>{k || '—'}</code>
      {porque !== undefined && <span className="why">{porque}</span>}
    </div>
  );

  return (
    <>
      <div className="crumb"><b>{slug}</b><span className="sep">/</span>saúde</div>
      <div className="kb-head">
        <div>
          <div className="sec-title">Saúde do acervo</div>
          <div className="sec-sub">
            O que decide se este acervo é confiável: quanto do código está documentado, quanto
            envelheceu em relação ao commit indexado, o que não tem dono, o que a IA não achou e o
            que espera revisão.
          </div>
        </div>
        <div className="kb-actions">
          <button
            type="button"
            className="btn ghost sm"
            onClick={async () => {
              avisar(await copiar(`eng health ${slug}`)
                ? 'comando copiado'
                : 'não consegui copiar — selecione o texto e use Ctrl+C');
            }}
          >
            copiar <code>eng health</code>
          </button>
          {admin && (
            <button
              type="button"
              className="btn ghost sm"
              disabled={reindexar.isPending}
              onClick={() => reindexar.mutate()}
            >
              {reindexar.isPending ? 'indexando…' : 'reindexar busca'}
            </button>
          )}
        </div>
      </div>

      <div className="hcards">
        <div className="hcard">
          <div className="n">{h.coverage_pct}%</div><div className="l">cobertura de doc</div>
          <div className="bar"><i style={{ width: `${h.coverage_pct}%` }} /></div>
        </div>
        <div className={`hcard${t.pending_proposals ? ' alert' : ''}`}>
          <div className="n">{t.pending_proposals}</div><div className="l">propostas pendentes</div>
        </div>
        <div className={`hcard${h.stale_docs.length ? ' alert' : ''}`}>
          <div className="n">{h.stale_docs.length}</div><div className="l">docs atrás do HEAD</div>
        </div>
        <div className={`hcard${h.without_owner.length ? ' bad' : ''}`}>
          <div className="n">{h.without_owner.length}</div><div className="l">arquivos sem dono</div>
        </div>
        <div className={`hcard${t.open_gaps ? ' alert' : ''}`}>
          <div className="n">{t.open_gaps}</div><div className="l">lacunas abertas</div>
        </div>
        <div className="hcard"><div className="n">{t.notes}</div><div className="l">notas do time</div></div>
        <div className="hcard">
          <div className="n">{t.decisions + t.runbooks + t.padroes}</div>
          <div className="l">decisões e runbooks</div>
        </div>
        <div className={`hcard${h.unanswered_searches.length ? ' alert' : ''}`}>
          <div className="n">{h.unanswered_searches.length}</div><div className="l">buscas sem resposta</div>
        </div>
      </div>

      <div className="sec-title" style={{ fontSize: 16, marginTop: 18 }}>Memória viva</div>
      <div className="sec-sub">
        O acervo só é confiável se alguém revalida. Estes quatro números dizem se a memória do time
        está viva ou apodrecendo — e se o que está no ar é o que está documentado.
      </div>
      <div className="hcards">
        <div className={`hcard${t.notes_to_review ? ' alert' : ''}`}>
          <div className="n">{t.notes_to_review || 0}</div><div className="l">notas a revisar</div>
          <div className="bar-l">o trecho que a nota citava mudou no código</div>
        </div>
        <div className={`hcard${sessoesVelhas.length ? ' alert' : ''}`}>
          <div className="n">{sessoesVelhas.length}</div><div className="l">sessões desatualizadas</div>
          <div className="bar-l">arquivo mudou depois do resumo</div>
        </div>
        <div className={`hcard${rc.recall_pct != null && rc.recall_pct < 70 ? ' alert' : ''}`}>
          <div className="n">{rc.recall_pct != null ? `${rc.recall_pct}%` : '—'}</div>
          <div className="l">taxa de resposta da busca</div>
          <div className="bar-l">
            {rc.searches
              ? `${rc.answered}/${rc.searches} buscas em ${rc.window_days} dias com pelo menos um hit`
              : 'nenhuma busca nos últimos dias'}
          </div>
        </div>
        <div className={`hcard${env.sha && env.aligned === false ? ' bad' : ''}`}>
          <div className="n" style={{ fontSize: 18 }}>{env.sha ? shaCurto(env.sha) : '—'}</div>
          <div className="l">ambiente vs acervo</div>
          <div className="bar-l">
            {env.sha
              ? `${env.env || ''} no ar · acervo @${shaCurto(h.head)}${
                env.aligned ? ' · alinhado' : ' · divergente: rota nova pode não existir no ambiente'
              }`
              : 'nenhum deploy registrado — o pipeline do projeto ainda não chama POST /deployment'}
          </div>
        </div>
      </div>

      {!!t.unconfirmed_sessions && (
        <div className="warn-line">
          {t.unconfirmed_sessions} resumo(s) de sessão esperando confirmação —{' '}
          <button type="button" className="lnkbtn" onClick={() => navegar(`/p/${slug}/sessions`)}>
            abrir Sessões
          </button>.
        </div>
      )}

      {!!sessoesVelhas.length && (
        <Secao titulo="Sessões desatualizadas">
          {sessoesVelhas.slice(0, 20).map((s) => (
            <Chave
              key={s.unit_key}
              k={s.unit_key}
              porque={`${s.task || ''} · mudou depois do resumo: ${(s.changed_files || []).join(', ')}`}
            />
          ))}
        </Secao>
      )}

      {!!notasARevisar.length && (
        <Secao titulo="Notas a revisar">
          {notasARevisar.slice(0, 20).map((n, i) => (
            <Chave
              key={`${n.unit_key}-${i}`}
              k={n.unit_key}
              porque={`${n.author_email || '—'} · o trecho citado saiu do arquivo`}
            />
          ))}
        </Secao>
      )}

      <Donos slug={slug} total={t.owners} consulta={donos} />

      <Secao titulo="Lacunas abertas">
        {lacunas.error
          ? <div className="muted" style={{ fontSize: 12.5 }}>Não deu para ler as lacunas do projeto.</div>
          : lacunas.isPending
            ? <Carregando rotulo="as lacunas abertas" />
            : (lacunas.data!.gaps.length || lacunas.data!.detected.length)
              ? (
                <>
                  {lacunas.data!.gaps.map((g) => (
                    <div className="hrow" key={g.id}>
                      <span className="tag">{rotulo(ESPECIE_DE_LACUNA, g.kind)}</span>
                      <code title={g.unit_key || ''} {...acionavel(() => g.unit_key && abrirDoc(g.unit_key))}>
                        {g.unit_key || '—'}
                      </code>
                      <span className="why">
                        {g.detail} · {g.origin || ''}{' '}
                        <button
                          type="button"
                          className="lnkbtn ok"
                          disabled={fecharLacuna.isPending}
                          onClick={() => fecharLacuna.mutate(g.id)}
                        >
                          resolver
                        </button>
                      </span>
                    </div>
                  ))}
                  {/* detectada não tem id no banco: é diagnóstico do detector, e
                      por isso não dá para "resolver" — só documentar */}
                  {lacunas.data!.detected.slice(0, 25).map((g, i) => (
                    <div className="hrow" key={`d-${g.unit_key}-${i}`} style={{ opacity: 0.85 }}>
                      <span className="tag">{rotulo(ESPECIE_DE_LACUNA, g.kind)}</span>
                      <code title={g.unit_key || ''} {...acionavel(() => g.unit_key && abrirDoc(g.unit_key))}>
                        {g.unit_key || '—'}
                      </code>
                      <span className="why">{g.detail} · detectada</span>
                    </div>
                  ))}
                </>
              )
              : (
                <div className="muted" style={{ fontSize: 12.5 }}>
                  Nada aberto — e o detector também não achou buraco.
                </div>
              )}
      </Secao>

      <Secao titulo="Doc atrás do código">
        {h.stale_docs.length
          ? h.stale_docs.slice(0, 30).map((s) => (
            <Chave
              key={s.unit_key}
              k={s.unit_key}
              porque={`gerada em @${shaCurto(s.source_sha)}${s.age_days != null ? ` · ${s.age_days} dia(s)` : ''}`}
            />
          ))
          : <div className="muted" style={{ fontSize: 12.5 }}>Toda doc está no commit indexado.</div>}
      </Secao>

      <Secao titulo="No grafo e sem doc">
        {h.missing_doc.length
          ? h.missing_doc.slice(0, 30).map((k) => <Chave key={k} k={k} porque="nunca documentado" />)
          : <div className="muted" style={{ fontSize: 12.5 }}>Todo arquivo do grafo tem doc.</div>}
      </Secao>

      <Secao titulo="Buscas sem resposta">
        {h.unanswered_searches.length
          ? h.unanswered_searches.map((s) => (
            <div className="hrow" key={s.query}>
              <code
                title={s.query}
                {...acionavel(() => navegar(`/p/${slug}/search?q=${encodeURIComponent(s.query)}`))}
              >
                {s.query}
              </code>
              <span className="why">{s.hits}× · ninguém achou</span>
            </div>
          ))
          : <div className="muted" style={{ fontSize: 12.5 }}>Toda busca recente devolveu algo.</div>}
      </Secao>

      {!!h.drifted_decisions.length && (
        <Secao titulo="Decisões com deriva">
          {h.drifted_decisions.map((d) => (
            <Chave key={d.unit_key} k={d.unit_key} porque={`cita ${d.missing.join(', ')}`} />
          ))}
        </Secao>
      )}
    </>
  );
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="hsec">
      <div className="sec-title" style={{ fontSize: 16 }}>{titulo}</div>
      <div className="hlist">{children}</div>
    </div>
  );
}

/**
 * Donos por glob: é o que faz `who_owns` responder no Cursor e o cabeçalho do
 * arquivo ter endereço. A importação de CODEOWNERS existe porque o time já
 * escreveu essa lista uma vez, e pedir para redigitar é como perder a regra.
 */
function Donos({ slug, total, consulta }: {
  slug: string;
  total: number;
  consulta: ReturnType<typeof useDonos>;
}) {
  const avisar = useRecado();
  const fila = useQueryClient();
  const { admin } = useSessao();
  const [padrao, setPadrao] = useState('');
  const [email, setEmail] = useState('');
  const [papel, setPapel] = useState('owner');
  const [codeowners, setCodeowners] = useState('');

  const recarregar = () => Promise.all([
    fila.invalidateQueries({ queryKey: chaves.donos(slug) }),
    fila.invalidateQueries({ queryKey: chaves.saude(slug) }),
  ]);

  const declarar = useMutation({
    mutationFn: () => postar(`/api/${slug}/owners`, {
      pattern: padrao.trim(), owner_email: email.trim(), role: papel,
    }),
    onSuccess: async () => {
      avisar('dono declarado');
      setPadrao(''); setEmail('');
      await recarregar();
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  const importar = useMutation({
    mutationFn: () => postar<{ imported?: number }>(`/api/${slug}/owners`, { codeowners: codeowners.trim() }),
    onSuccess: async (r) => {
      avisar(`${r.imported} regra(s) importada(s)`);
      setCodeowners('');
      await recarregar();
    },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  const remover = useMutation({
    mutationFn: (id: string) => apagar(`/api/${slug}/owners/${encodeURIComponent(id)}`),
    onSuccess: async () => { avisar('dono removido'); await recarregar(); },
    onError: (e) => avisar(e instanceof Error ? e.message : String(e)),
  });

  const lista = total ? (consulta.data || []) : [];

  return (
    <div className="hsec">
      <div className="sec-title" style={{ fontSize: 16 }}>Donos de módulo</div>
      <div className="sec-sub">
        Padrão glob → e-mail. É o que faz <code>who_owns</code> responder no Cursor e o cabeçalho do
        arquivo ter endereço.
      </div>
      {admin
        ? (
          <>
            <div className="own-grid">
              <label className="sr-only" htmlFor="ow-pat">padrão glob</label>
              <input
                id="ow-pat"
                placeholder="src/messages/** ou **/*.controller.ts"
                value={padrao}
                onChange={(e) => setPadrao(e.target.value)}
              />
              <label className="sr-only" htmlFor="ow-mail">e-mail do dono</label>
              <input
                id="ow-mail"
                placeholder="dono@extreme.digital"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <label className="sr-only" htmlFor="ow-role">papel</label>
              <select id="ow-role" value={papel} onChange={(e) => setPapel(e.target.value)}>
                <option value="owner">dono</option>
                <option value="reviewer">revisor</option>
              </select>
              <button
                type="button"
                className="btn"
                disabled={declarar.isPending}
                onClick={() => declarar.mutate()}
              >
                Adicionar
              </button>
            </div>
            <div className="own-grid" style={{ gridTemplateColumns: '1fr auto', marginTop: 8 }}>
              <label className="sr-only" htmlFor="ow-co">conteúdo do CODEOWNERS</label>
              <input
                id="ow-co"
                placeholder="colar CODEOWNERS para importar (uma regra por linha)"
                value={codeowners}
                onChange={(e) => setCodeowners(e.target.value)}
              />
              <button
                type="button"
                className="btn ghost"
                disabled={importar.isPending}
                onClick={() => {
                  if (!codeowners.trim()) { avisar('cole o conteúdo do CODEOWNERS'); return; }
                  importar.mutate();
                }}
              >
                Importar
              </button>
            </div>
          </>
        )
        : <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>Só admin declara donos.</div>}

      <div className="hlist" style={{ marginTop: 10 }}>
        {lista.length
          ? lista.map((o) => (
            <div className="hrow" key={o.id}>
              <code>{o.pattern}</code>
              <span>{o.owner_email}</span>
              <span className="why">
                {o.role}
                {admin && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="lnkbtn bad"
                      disabled={remover.isPending}
                      onClick={() => remover.mutate(String(o.id))}
                    >
                      remover
                    </button>
                  </>
                )}
              </span>
            </div>
          ))
          : (
            <div className="muted" style={{ fontSize: 12.5 }}>
              Nenhum dono declarado — todo arquivo aparece como órfão no Cursor e a pergunta “quem
              sabe disso?” não tem resposta.
              {admin
                ? ' Declare o primeiro no formulário acima, ou cole o CODEOWNERS do repositório.'
                : ' Peça a um admin para declarar.'}
            </div>
          )}
      </div>
    </div>
  );
}
